import dayjs from "dayjs";
import {Checkbox} from "@/components/ui/checkbox";
import {Badge} from "@/components/ui/badge";
import {Card, CardContent} from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Button} from "@/components/ui/button";
import {Eye, MoreHorizontal, Printer, Trash2, Users} from "lucide-react";
import type {PersonModelInterface} from "@/models/personModel";

function getStatusBadge(status: string) {
    const map: Record<string, { label: string; className: string }> = {
        new: {label: "Новый", className: "bg-green-100 text-green-700 border border-green-200"},
        pending: {label: "В процессе", className: "bg-yellow-100 text-yellow-700 border border-yellow-200"},
        overdue: {label: "Просрочено", className: "bg-red-100 text-red-700 border border-red-200"},
        closed: {label: "Закрыт", className: "bg-red-100 text-red-700 border border-red-200"},
        closedEarly: {label: "Закрыт досрочно", className: "bg-red-100 text-red-700 border border-red-200"},
        returned: {label: "Возврат", className: "bg-red-100 text-red-700 border border-red-200"},
    };

    const c = map[status] || {label: status, className: "bg-gray-100 text-gray-700 border border-gray-200"};

    return (
        <Badge variant="outline" className={c.className}>
            {c.label}
        </Badge>
    );
}

export function ClientsTable(props: {
    people: PersonModelInterface[];
    selectedIds: string[];
    onSelect: (id: string, checked: boolean) => void;
    onSelectAll: (checked: boolean) => void;
    sortBy: string;
    sortDirection: "" | "asc" | "desc";
    onSort: (key: string) => void;
    onView: (id: string) => void;
    onPrint: (id: string) => void;
    // ✅ теперь НЕ удаляем сразу, а открываем модалку (внешний обработчик)
    onDelete: (id: string) => void;
}) {
    const {people, selectedIds} = props;

    if (people.length === 0) {
        return (
            <Card>
                <CardContent className="py-12">
                    <div className="text-center text-muted-foreground">
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50"/>
                        <p>Нет клиентов в этой категории</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const allSelected = people.length > 0 && people.every((p) => selectedIds.includes(p.id));

    const RowMenu = ({id}: { id: string }) => (
        <>
            {/* Dropdown (кнопка три точки) */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4"/>
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={() => props.onView(id)}>
                        <Eye className="h-4 w-4 mr-2"/>
                        Просмотр
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => props.onPrint(id)}>
                        <Printer className="h-4 w-4 mr-2"/>
                        Договор
                    </DropdownMenuItem>

                    <DropdownMenuSeparator/>

                    <DropdownMenuItem
                        onClick={() => props.onDelete(id)} // ✅ открываем модалку
                        className="text-destructive focus:text-destructive"
                    >
                        <Trash2 className="h-4 w-4 mr-2"/>
                        Удалить
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Context Menu (ПКМ) */}
            <ContextMenuContent onClick={(e) => e.stopPropagation()}>
                <ContextMenuItem onClick={() => props.onView(id)}>
                    <Eye className="h-4 w-4 mr-2"/>
                    Просмотр
                </ContextMenuItem>

                <ContextMenuItem onClick={() => props.onPrint(id)}>
                    <Printer className="h-4 w-4 mr-2"/>
                    Договор
                </ContextMenuItem>

                <ContextMenuSeparator/>

                <ContextMenuItem
                    onClick={() => props.onDelete(id)} // ✅ открываем модалку
                    className="text-destructive focus:text-destructive"
                >
                    <Trash2 className="h-4 w-4 mr-2"/>
                    Удалить
                </ContextMenuItem>
            </ContextMenuContent>
        </>
    );

    return (
        <Card>
            <CardContent className="p-0">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[44px]">
                                    <Checkbox
                                        checked={allSelected}
                                        onCheckedChange={(v) => props.onSelectAll(Boolean(v))}
                                    />
                                </TableHead>

                                <TableHead className="cursor-pointer" onClick={() => props.onSort("name")}>
                                    Клиент{" "}
                                    {props.sortBy === "name"
                                        ? props.sortDirection === "asc"
                                            ? "↑"
                                            : props.sortDirection === "desc"
                                                ? "↓"
                                                : ""
                                        : ""}
                                </TableHead>

                                <TableHead className="hidden md:table-cell">Телефон</TableHead>

                                <TableHead className="text-right cursor-pointer" onClick={() => props.onSort("total")}>
                                    Сумма{" "}
                                    {props.sortBy === "total"
                                        ? props.sortDirection === "asc"
                                            ? "↑"
                                            : props.sortDirection === "desc"
                                                ? "↓"
                                                : ""
                                        : ""}
                                </TableHead>

                                <TableHead
                                    className="hidden lg:table-cell cursor-pointer"
                                    onClick={() => props.onSort("releaseDay")}
                                >
                                    Дата выдачи{" "}
                                    {props.sortBy === "releaseDay"
                                        ? props.sortDirection === "asc"
                                            ? "↑"
                                            : props.sortDirection === "desc"
                                                ? "↓"
                                                : ""
                                        : ""}
                                </TableHead>

                                <TableHead>Статус</TableHead>
                                <TableHead className="w-[50px]"/>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {people.map((p) => (
                                <ContextMenu key={p.id}>
                                    <ContextMenuTrigger asChild>
                                        <TableRow
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => props.onView(p.id)}
                                        >
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedIds.includes(p.id)}
                                                    onCheckedChange={(v) => props.onSelect(p.id, Boolean(v))}
                                                />
                                            </TableCell>

                                            <TableCell>
                                                <div className="font-medium">{p.name}</div>
                                                <div className="text-xs text-muted-foreground md:hidden">
                                                    {p.detailInfo?.phoneNumber || "—"}
                                                </div>
                                            </TableCell>

                                            <TableCell className="hidden md:table-cell">
                                                {p.detailInfo?.phoneNumber || "—"}
                                            </TableCell>

                                            <TableCell className="text-right font-medium">
                                                {p.creditDetail?.total ?? 0}
                                            </TableCell>

                                            <TableCell className="hidden lg:table-cell">
                                                {p.creditDetail?.releaseDay
                                                    ? dayjs(p.creditDetail.releaseDay).format("DD-MM-YYYY")
                                                    : "—"}
                                            </TableCell>

                                            <TableCell>{getStatusBadge(p.status)}</TableCell>

                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <RowMenu id={p.id}/>
                                            </TableCell>
                                        </TableRow>
                                    </ContextMenuTrigger>

                                    {/* Context menu content находится внутри RowMenu */}
                                </ContextMenu>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}
